import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, TitleText } from '@garnish/core';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderStatusContactUs = (props: OrderStatusContactUsProps) => {
  const {
    orderId,
    restaurantId,
    canCancel,
    onContactUs,
    onCancelOrder,
    shouldHideCancelOption = false,
  } = props;

  const { formatMessage } = useIntl();

  const shouldDisplayCancelButton =
    canCancel && orderId !== undefined && restaurantId !== undefined && !shouldHideCancelOption;

  return (
    <View style={styles.cardContainer}>
      <TitleText style={styles.cardTitle} sizeMatch={['24']}>
        {formatMessage(messages.heading)}
      </TitleText>

      <BodyText style={styles.cardBody} sizeMatch={['16']}>
        {formatMessage(messages.subheading)}
      </BodyText>

      <View style={styles.cardCtaContainer}>
        <Button palette="primary" onPress={onContactUs}>
          {formatMessage(messages.cta)}
        </Button>

        {shouldDisplayCancelButton ? (
          <Button palette="secondary" onPress={onCancelOrder}>
            {formatMessage(messages.cancelOrderCta)}
          </Button>
        ) : null}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.QUINOA,
    marginBottom: theme.spacing['4'],
    padding: theme.spacing['6'],
  },
  cardTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  cardBody: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  cardCtaContainer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type OrderStatusContactUsProps = {
  orderId?: string;
  restaurantId?: string;
  canCancel: boolean;
  onContactUs: () => void;
  onCancelOrder?: () => void;
  shouldHideCancelOption?: boolean;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  heading: {
    defaultMessage: 'Need help with your order?',
    description: 'Order Status | Contact Us Card | Card heading',
  },
  subheading: {
    defaultMessage: 'We want to hear from you.',
    description: 'Order Status | Contact Us Card | Card subheading',
  },
  cta: {
    defaultMessage: 'Contact us',
    description: 'Order Status | Contact Us Card | Contact us button',
  },
  cancelOrderCta: {
    defaultMessage: 'Cancel order',
    description: 'Order Status | Contact Us Card | Cancel order button',
  },
});
